/**
 * PlaybookRunBanner
 *
 * Shown at the top of CopilotPage when the current thread is a playbook run
 * (agent_type === 'playbook'). Provides:
 *   - Visual context: "Agent run: <name>"
 *   - Live status derived from the copilot store's isStreaming flag
 *   - Back-link to the Playbook detail page
 *   - Download button once document_uri is available (polled from single-run endpoint)
 *
 * Uses isStreaming from the store for the "running" state — no separate polling
 * needed during active execution. A lightweight single-run fetch runs once on
 * mount and after streaming stops to pick up the completed document URI.
 */

import React, { useContext, useEffect, useState } from 'react';
import { Box, Button, Chip, CircularProgress, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { SnackbarContext } from '../../../ui/SnackbarProvider';
import ArrowBackRounded from '@mui/icons-material/ArrowBackRounded';
import CloseRounded from '@mui/icons-material/Close';
import DownloadRounded from '@mui/icons-material/DownloadRounded';
import SmartToyOutlined from '@mui/icons-material/SmartToyOutlined';
import { useCopilotStore } from '../stores/useCopilotStore';
import { getPlaybookRun, getDocumentDownloadUrl } from '../../../services/playbooksService';
import type { PlaybookRun, RunArtifact } from '../../../types/playbooks';

interface Props {
  playbookId: string;
  playbookName: string | null;
  runId: string;
}

export default function PlaybookRunBanner({ playbookId, playbookName, runId }: Props) {
  const theme = useTheme();
  const navigate = useNavigate();
  const { notify } = useContext(SnackbarContext);
  const [dismissed, setDismissed] = useState(false);
  const [run, setRun] = useState<PlaybookRun | null>(null);
  const [downloading, setDownloading] = useState(false);

  // Drive the "running" visual from the live stream state, not a separate poll
  const isStreaming = useCopilotStore((s: any) => s.isStreaming) as boolean;

  // Fetch run metadata once on mount and again after streaming stops
  // to pick up the completed status + document_uri
  useEffect(() => {
    getPlaybookRun(playbookId, runId).then(setRun).catch(() => {});
  }, [playbookId, runId]);

  useEffect(() => {
    if (!isStreaming) {
      // Brief delay so the backend has time to commit the completed status
      const t = setTimeout(() => {
        getPlaybookRun(playbookId, runId).then(setRun).catch(() => {});
      }, 1500);
      return () => clearTimeout(t);
    }
  }, [isStreaming, playbookId, runId]);

  if (dismissed) return null;

  const tokens = theme.tokens;

  const isCompleted = !isStreaming && run?.status === 'completed';
  const isFailed = !isStreaming && run?.status === 'failed';
  const isRunning = isStreaming || (!run) || run.status === 'running' || run.status === 'pending';

  const accentColor = isFailed
    ? tokens.semantic.danger
    : isCompleted
    ? tokens.semantic.success
    : tokens.semantic.info ?? theme.palette.info.main;
  const accentSoft = isFailed
    ? tokens.semantic.dangerSoft
    : isCompleted
    ? tokens.semantic.successSoft
    : tokens.semantic.infoSoft ?? theme.palette.info.light;

  const statusLabel = isFailed ? 'Failed' : isCompleted ? 'Completed' : 'Running';
  const statusColor: 'error' | 'success' | 'info' = isFailed ? 'error' : isCompleted ? 'success' : 'info';

  const outputs = run?.outputs || [];
  const canDownload = isCompleted && outputs.length > 0;

  const handleDownload = async (artifact: RunArtifact) => {
    if (downloading) return;
    setDownloading(true);
    try {
      const url = getDocumentDownloadUrl(playbookId, runId, artifact.artifact_id);
      const token = localStorage.getItem('klaris_jwt');
      const res = await fetch(url, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) throw new Error(`Download failed (${res.status})`);
      const blob = await res.blob();
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      const safeName = (playbookName || 'agent_report').replace(/[^a-z0-9_\-]/gi, '_').toLowerCase();
      const fmt = artifact.format || 'docx';
      const ext = fmt === 'xlsx' ? 'xlsx' : fmt === 'pptx' ? 'pptx' : fmt === 'pdf' ? 'pdf' : 'docx';
      link.download = `${safeName}.${ext}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(link.href);
    } catch (e: any) {
      notify(e?.message || 'Download failed', 'error');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Box sx={{ flexShrink: 0, bgcolor: accentSoft, borderBottom: `1px solid ${tokens.border.subtle}` }}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          px: { xs: 1.5, sm: 2, md: 3 },
          py: 0.75,
          alignItems: { xs: 'stretch', sm: 'center' },
          gap: { xs: 0.75, sm: 1.5 },
          minHeight: 40,
        }}
      >
        {/* Icon + run info — one row that can shrink/truncate */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: 0 }}>
          {/* Icon bubble */}
          <Box
            sx={{
              flexShrink: 0,
              width: 22,
              height: 22,
              borderRadius: '50%',
              bgcolor: accentColor,
              color: '#fff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <SmartToyOutlined sx={{ fontSize: 13 }} />
          </Box>

          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            sx={{ flex: 1, minWidth: 0, gap: 0.5 }}
          >
            <Typography
              variant="body2"
              fontWeight={600}
              sx={{
                fontSize: '0.8125rem',
                color: tokens.text.primary,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                minWidth: 0,
              }}
            >
              Agent run{playbookName ? `: ${playbookName}` : ''}
            </Typography>
            <Chip
              label={statusLabel}
              color={statusColor}
              size="small"
              sx={{ flexShrink: 0, height: 18, fontSize: '0.6875rem', fontWeight: 700 }}
            />
            {isRunning && (
              <Typography
                variant="body2"
                sx={{ display: { xs: 'none', md: 'block' }, fontSize: '0.8125rem', color: tokens.text.secondary, whiteSpace: 'nowrap' }}
              >
                · Agent is working towards your goal…
              </Typography>
            )}
            {isFailed && (
              <Typography
                variant="body2"
                sx={{ display: { xs: 'none', md: 'block' }, fontSize: '0.8125rem', color: tokens.text.secondary, whiteSpace: 'nowrap' }}
              >
                · Something went wrong. Check the chat above for details.
              </Typography>
            )}
          </Stack>
        </Box>

        <Stack
          direction="row"
          spacing={0.75}
          alignItems="center"
          sx={{ flexShrink: 0, alignSelf: { xs: 'flex-end', sm: 'auto' }, ml: { sm: 'auto' } }}
        >
          <Button
            size="small"
            variant="text"
            startIcon={<ArrowBackRounded sx={{ fontSize: 14 }} />}
            onClick={() => navigate(`/agents/${playbookId}`)}
            sx={{ fontSize: '0.75rem', textTransform: 'none', py: 0.25, px: 1 }}
          >
            View agent
          </Button>

          {canDownload && outputs.map((out) => {
            const label = outputs.length > 1
              ? (out.format === 'xlsx' ? 'Excel' : out.format === 'pptx' ? 'PPT' : 'Word')
              : 'Download';
            return (
              <Tooltip key={out.artifact_id} title={`Download ${label.toLowerCase()}`}>
                <Button
                  size="small"
                  variant="outlined"
                  color="success"
                  startIcon={downloading ? <CircularProgress size={12} color="inherit" /> : <DownloadRounded sx={{ fontSize: 14 }} />}
                  onClick={() => handleDownload(out)}
                  disabled={downloading}
                  sx={{ fontSize: '0.75rem', textTransform: 'none', py: 0.25, px: 1 }}
                >
                  {label}
                </Button>
              </Tooltip>
            );
          })}

          <Tooltip title="Dismiss">
            <IconButton
              size="small"
              onClick={() => setDismissed(true)}
              sx={{ color: tokens.text.tertiary, '&:hover': { color: tokens.text.primary } }}
            >
              <CloseRounded sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        </Stack>
      </Box>
    </Box>
  );
}
